import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Components
import IconWithBackground from "new_components/molecules/IconWithBackground";
import Text from "new_components/atoms/Text";
import ImageButton from "new_components/molecules/ImageButton";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Card Wrapper
const CardWrapper = styled.div`
  width: ${({ wideCard, extraWide, smallCard }) =>
    extraWide
      ? "calc(50% - 40px)"
      : wideCard
      ? "calc(40% - 20px)"
      : smallCard
      ? "calc(30% - 20px)"
      : "calc(33% - 20px)"};
  background-color: #f8faff;
  border-radius: 5px;
  padding: 20px;
  margin-right: 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease, outline 0.2s ease;
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  outline: ${({ selected }) =>
    selected ? `2px solid ${colors.primary[500]}` : "none"};

  &:hover {
    background-color: #ebf1fd;
  }
`;

// Content Section
const Content = styled.div`
  flex: 1;
  margin-bottom: 10px;
`;

// Footer Section
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Card = ({
  iconName,
  iconBgColor,
  title,
  description,
  linkText,
  reportLength,
  onClick,
  noFooter = false,
  wideCard = false,
  extraWide = false,
  smallCard = false,
  customFooter,
  subtext,
  selected = false,
  footerText,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <CardWrapper
      wideCard={wideCard}
      extraWide={extraWide}
      smallCard={smallCard}
      isClickable={!!onClick}
      onClick={onClick ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      selected={selected}
    >
      {/* Header */}
      <IconWithBackground
        name={iconName}
        onClick={onClick}
        bgColor={iconBgColor}
        hoverParent={isHovered} // Pass hover state to IconWithBackground
      />

      <div
        style={{
          marginTop: 15,
          marginBottom: 10,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Text typographyStyle={typography.body.semibold}>{title}</Text>
        {subtext && (
          <Text
            typographyStyle={typography.smallText.regular}
            color={colors.text.gray}
          >
            {subtext}
          </Text>
        )}
      </div>

      {/* Content */}
      <Content>
        <Text
          typographyStyle={typography.smallText.regular}
          color={subtext ? colors.text.dark : colors.text.gray}
        >
          {description}
        </Text>
      </Content>

      {/* Footer */}
      {!noFooter && (
        <Footer>
          {linkText ? (
            <ImageButton
              text={linkText}
              icon={ICON_SET.chevronRight}
              iconPosition="right"
              textStyle
              onClick={onClick}
              color={colors.primary[500]}
            />
          ) : (
            <Text
              typographyStyle={typography.smallText.regular}
              color={colors.text.gray}
            >
              Report length:{" "}
              <Text typographyStyle={typography.smallText.semibold}>
                  {reportLength} {reportLength === 1 ? "page" : "pages"}
              </Text>
            </Text>
          )}
        </Footer>
      )}
      {customFooter && customFooter}
      {footerText && (
        <div
          style={{
            marginTop: 15,
            marginBottom: 10,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {footerText && (
            <Text
              typographyStyle={typography.smallText.regular}
              color={colors.text.gray}
            >
              {footerText}
            </Text>
          )}
        </div>
      )}
    </CardWrapper>
  );
};

Card.propTypes = {
  iconName: PropTypes.string.isRequired,
  iconBgColor: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  linkText: PropTypes.string,
  reportLength: PropTypes.string,
  selected: PropTypes.bool,
};

Card.defaultProps = {
  iconBgColor: colors.primary[50],
  description: "",
  linkText: null,
  reportLength: null,
  selected: false,
};

export default Card;